/**
 * HarvestMonitoringTable Component
 * Displays multi-level harvest monitoring data:
 * Level 1: Estate Summary Card (Top KPI + 1-row Summary Table + Expand Toggle)
 * Level 2: Division Cards (Top KPI + Detail Table per Division)
 * Level 3: TPH Detail Rows (Expandable per Block / SPB with Image Previews)
 */

import React, { useState } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Text,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    HStack,
    IconButton,
    VStack,
    Image,
    Flex,
    Collapse,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@chakra-ui/icons';
import angkongIcon from '../../../assets/icons/angkong_icon.svg';
import cautionIcon from '../../../assets/icons/warning.png';
import percentIcon from '../../../assets/icons/percentage_icon.png';
import dtIcon from '../../../assets/icons/dump-truck_icon.svg';
import type {
    HarvestMonitoringEstateRecord,
    SPBEstateRecord,
    HarvestMonitoringSubtotal,
} from '../types';
import { formatDate, formatNumber, formatInteger } from '../utils';
import { TphDetailRows } from './TphDetailRows';
import { useImagePreview, ImagePreviewModal } from './ImagePreview';

interface HarvestMonitoringTableProps {
    data: HarvestMonitoringEstateRecord[];
    spbData?: SPBEstateRecord[];
    totals?: HarvestMonitoringSubtotal | null;
    viewMode?: 'block' | 'spb';
    loading: boolean;
    error: string | null;
}

export const HarvestMonitoringTable: React.FC<HarvestMonitoringTableProps> = ({
    data,
    spbData = [],
    viewMode = 'block',
    loading,
    error,
}) => {
    // Track expanded state for Estate cards (Level 1)
    const [expandedEstates, setExpandedEstates] = useState<Record<string, boolean>>({});
    // Track expanded state for TPH rows (Level 3)
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const { isOpen, onClose, previewImageUrl, previewImageTitle, handlePreviewImage } = useImagePreview();

    const isSingleEstateBlock = data && data.length === 1;
    const isSingleEstateSPB = spbData && spbData.length === 1;

    const toggleEstate = (estateKey: string, defaultExpanded: boolean) => {
        setExpandedEstates(prev => {
            const current = prev[estateKey] !== undefined ? prev[estateKey] : defaultExpanded;
            return {
                ...prev,
                [estateKey]: !current,
            };
        });
    };

    const isEstateOpen = (estateKey: string, defaultExpanded: boolean) => {
        return expandedEstates[estateKey] !== undefined ? expandedEstates[estateKey] : defaultExpanded;
    };

    const toggleRow = (rowKey: string) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowKey]: !prev[rowKey],
        }));
    };

    if (loading) {
        return (
            <Center py={10}>
                <Spinner size="xl" color="green.500" thickness="4px" />
            </Center>
        );
    }

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Box>
            </Alert>
        );
    }

    if (viewMode === 'block' && (!data || data.length === 0)) {
        return (
            <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>Tidak ada data untuk ditampilkan</AlertDescription>
            </Alert>
        );
    }

    if (viewMode === 'spb' && (!spbData || spbData.length === 0)) {
        return (
            <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>Tidak ada data SPB untuk ditampilkan</AlertDescription>
            </Alert>
        );
    }

    // ==========================================
    // SPB VIEW
    // ==========================================
    if (viewMode === 'spb') {
        return (
            <VStack spacing={8} align="stretch">
                {spbData.map((estate) => {
                    const estateKey = `spb-estate-${estate.estate_id || estate.estate_name}`;
                    const isOpenEstate = isEstateOpen(estateKey, isSingleEstateSPB);
                    const totalSPBInEstate = estate.divisions.reduce((sum, d) => sum + d.rows.length, 0);

                    return (
                        <Box key={estateKey}>
                            {/* LEVEL 1: ESTATE CARD */}
                            <Box borderRadius="lg" boxShadow="sm" overflow="hidden" bg="white" border="1px solid" borderColor="green.200">
                                <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                                    <Box
                                        bg="#dbe7d0"
                                        p={4}
                                        flex="2.5"
                                        cursor="pointer"
                                        onClick={() => toggleEstate(estateKey, isSingleEstateSPB)}
                                        _hover={{ bg: '#d0dfc3' }}
                                        transition="background 0.2s"
                                    >
                                        <HStack justify="space-between" align="center">
                                            <Box>
                                                <Text color="black" fontSize="md">
                                                    <b>ESTATE | - </b>{estate.estate_name || 'TANPA KEBUN'}
                                                </Text>
                                                <Text color="gray.700" fontSize="xs">
                                                    {estate.divisions.length} Divisi | {totalSPBInEstate} SPB Terdaftar
                                                </Text>
                                            </Box>
                                            <IconButton
                                                aria-label="Toggle Estate"
                                                icon={isOpenEstate ? <ChevronDownIcon boxSize={5} /> : <ChevronRightIcon boxSize={5} />}
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="green"
                                            />
                                        </HStack>
                                    </Box>

                                    <Box bg="white" p={4} flex="1">
                                        <HStack spacing={4}>
                                            <Box bg="#ededed" p={2} borderRadius="lg">
                                                <Image src={angkongIcon} alt="Angkong" boxSize="25px" />
                                            </Box>
                                            <Box>
                                                <Text color="black" fontSize="xs"><b>Hasil Panen </b>BCC</Text>
                                                <Text color="black" fontSize="sm"><b>{formatInteger(estate.subtotal.total_bcc_weight)} </b>Kg</Text>
                                            </Box>
                                        </HStack>
                                    </Box>

                                    <Box bg="white" p={4} flex="1">
                                        <HStack spacing={4}>
                                            <Box bg="#ededed" p={2} borderRadius="lg">
                                                <Image src={dtIcon} alt="SPB" boxSize="25px" />
                                            </Box>
                                            <Box>
                                                <Text color="black" fontSize="xs"><b>Total SPB </b></Text>
                                                <Text color="black" fontSize="sm"><b>{formatInteger(estate.subtotal.total_spb_weight)} </b>Kg</Text>
                                            </Box>
                                        </HStack>
                                    </Box>

                                    <Box bg="white" p={4} flex="1">
                                        <HStack spacing={4}>
                                            <Box bg="red.100" p={2} borderRadius="lg">
                                                <Image src={cautionIcon} alt="Caution" boxSize="25px" />
                                            </Box>
                                            <Box>
                                                <Text color="black" fontSize="xs"><b>Total Restan </b></Text>
                                                <Text color="black" fontSize="sm"><b>{formatInteger(estate.subtotal.total_restan_weight)} </b>Kg</Text>
                                            </Box>
                                        </HStack>
                                    </Box>
                                </Flex>

                                {/* 1-Row Estate Summary Table */}
                                <Box overflowX="auto" bg="#f7faf4" borderTop="1px solid" borderColor="green.200">
                                    <Table variant="unstyled" size="sm">
                                        <Thead>
                                            <Tr bg="#4A5A33">
                                                <Th colSpan={3} py={1.5} px={3}>
                                                    <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                        Janjang
                                                    </Text>
                                                </Th>
                                                <Th colSpan={3} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                    <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                        Berat Estimasi (kg)
                                                    </Text>
                                                </Th>
                                            </Tr>
                                            <Tr bg="#e7efdf" fontSize="xs">
                                                <Th py={1} isNumeric textAlign="right">BCC</Th>
                                                <Th py={1} isNumeric textAlign="right">SPB</Th>
                                                <Th py={1} isNumeric textAlign="right">Restan</Th>
                                                <Th py={1} borderLeft="1px solid" borderColor="blackAlpha.300" isNumeric textAlign="right">BCC</Th>
                                                <Th py={1} isNumeric textAlign="right">SPB</Th>
                                                <Th py={1} isNumeric textAlign="right">Restan</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            <Tr bg="white" borderTop="1px solid" borderColor="green.200">
                                                <Td py={2} isNumeric textAlign="right">
                                                    {formatInteger(estate.subtotal.total_bcc_bunch)}
                                                </Td>
                                                <Td py={2} isNumeric textAlign="right">
                                                    {formatInteger(estate.subtotal.total_spb_bunch)}
                                                </Td>
                                                <Td py={2} isNumeric textAlign="right">
                                                    {estate.subtotal.total_jjg_restan > 0 ? (
                                                        <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                                            {formatInteger(estate.subtotal.total_jjg_restan)}
                                                        </Badge>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </Td>
                                                <Td py={2} borderLeft="1px solid" borderColor="blackAlpha.300" isNumeric textAlign="right">
                                                    {formatNumber(estate.subtotal.total_bcc_weight)}
                                                </Td>
                                                <Td py={2} isNumeric textAlign="right">
                                                    {formatNumber(estate.subtotal.total_spb_weight)}
                                                </Td>
                                                <Td py={2} isNumeric textAlign="right">
                                                    {estate.subtotal.total_restan_weight > 0 ? (
                                                        <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                                            {formatNumber(estate.subtotal.total_restan_weight)}
                                                        </Badge>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </Td>
                                            </Tr>
                                        </Tbody>
                                    </Table>
                                </Box>
                            </Box>

                            {/* LEVEL 2: DIVISION CARDS & SPB TABLES */}
                            <Collapse in={isOpenEstate} animateOpacity>
                                <VStack spacing={6} align="stretch" mt={4} pl={{ base: 2, md: 4 }} borderLeft={{ base: 'none', md: '3px solid #c8dcb8' }}>
                                    {estate.divisions.map((division) => {
                                        const divName = division.division || 'TANPA DIVISI';
                                        return (
                                            <Box key={divName}>
                                                {/* Division Header Card */}
                                                <Box borderRadius="lg" boxShadow="sm" mb={3} overflow="hidden" bg="white">
                                                    <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                                                        <Box bg="#e7efdf" p={4} flex="2.5">
                                                            <Text color="black"><b>DIVISI | - </b>{divName}</Text>
                                                            <Text color="black" fontSize="xs">{division.rows.length} SPB Terdaftar</Text>
                                                        </Box>
                                                        <Box bg="white" p={4} flex="1">
                                                            <HStack spacing={4}>
                                                                <Box bg="#ededed" p={2} borderRadius="lg">
                                                                    <Image src={angkongIcon} alt="Angkong" boxSize="25px" />
                                                                </Box>
                                                                <Box>
                                                                    <Text color="black" fontSize="xs"><b>Hasil Panen </b>BCC</Text>
                                                                    <Text color="black" fontSize="sm"><b>{formatInteger(division.subtotal.total_bcc_weight)} </b>Kg</Text>
                                                                </Box>
                                                            </HStack>
                                                        </Box>
                                                        <Box bg="white" p={4} flex="1">
                                                            <HStack spacing={4}>
                                                                <Box bg="#ededed" p={2} borderRadius="lg">
                                                                    <Image src={dtIcon} alt="SPB" boxSize="25px" />
                                                                </Box>
                                                                <Box>
                                                                    <Text color="black" fontSize="xs"><b>Total SPB </b></Text>
                                                                    <Text color="black" fontSize="sm"><b>{formatInteger(division.subtotal.total_spb_weight)} </b>Kg</Text>
                                                                </Box>
                                                            </HStack>
                                                        </Box>
                                                        <Box bg="white" p={4} flex="1">
                                                            <HStack spacing={4}>
                                                                <Box bg="red.100" p={2} borderRadius="lg">
                                                                    <Image src={cautionIcon} alt="Caution" boxSize="25px" />
                                                                </Box>
                                                                <Box>
                                                                    <Text color="black" fontSize="xs"><b>Total Restan </b></Text>
                                                                    <Text color="black" fontSize="sm"><b>{formatInteger(division.subtotal.total_restan_weight)} </b>Kg</Text>
                                                                </Box>
                                                            </HStack>
                                                        </Box>
                                                    </Flex>
                                                </Box>

                                                {/* SPB Table for this division */}
                                                <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm">
                                                    <Table variant="unstyled" size="sm">
                                                        <Thead bg="white">
                                                            <Tr bg="#4A5A33">
                                                                <Th colSpan={8} py={2} px={3}>
                                                                    <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                        Informasi SPB
                                                                    </Text>
                                                                </Th>
                                                                <Th colSpan={3} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                                    <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                        Janjang
                                                                    </Text>
                                                                </Th>
                                                                <Th colSpan={3} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                                    <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                        Berat Estimasi (kg)
                                                                    </Text>
                                                                </Th>
                                                            </Tr>
                                                            <Tr bg="#e7efdf">
                                                                <Th>Tanggal</Th>
                                                                <Th>No. SPB</Th>
                                                                <Th>Divisi</Th>
                                                                <Th>Driver</Th>
                                                                <Th>No. Kendaraan</Th>
                                                                <Th whiteSpace="normal">Pemuat</Th>
                                                                <Th whiteSpace="normal">Pelangsir</Th>
                                                                <Th>No. Tiket</Th>
                                                                <Th borderLeft="1px solid" borderColor="blackAlpha.300" isNumeric textAlign="right">BCC</Th>
                                                                <Th isNumeric textAlign="right">SPB</Th>
                                                                <Th isNumeric textAlign="right">Restan</Th>
                                                                <Th borderLeft="1px solid" borderColor="blackAlpha.300" isNumeric textAlign="right">BCC</Th>
                                                                <Th isNumeric textAlign="right">SPB</Th>
                                                                <Th isNumeric textAlign="right">Restan</Th>
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            {division.rows.map((row) => {
                                                                const isExpanded = expandedRows[row.transport_id] || false;
                                                                return (
                                                                    <React.Fragment key={row.transport_id}>
                                                                        <Tr _hover={{ bg: 'gray.50' }} borderBottom="1px solid" borderColor="gray.100">
                                                                            <Td whiteSpace="nowrap">{formatDate(row.transport_date)}</Td>
                                                                            <Td>
                                                                                <HStack spacing={1}>
                                                                                    {row.tph_details && row.tph_details.length > 0 && (
                                                                                        <IconButton
                                                                                            aria-label="Expand row"
                                                                                            icon={isExpanded ? <ChevronDownIcon boxSize={4} /> : <ChevronRightIcon boxSize={4} />}
                                                                                            size="xs"
                                                                                            variant="ghost"
                                                                                            onClick={() => toggleRow(row.transport_id)}
                                                                                        />
                                                                                    )}
                                                                                    <Text fontWeight="bold" fontSize="xs">{row.transport_nbr || '(Belum SPB)'}</Text>
                                                                                </HStack>
                                                                            </Td>
                                                                            <Td>{row.division}</Td>
                                                                            <Td>{row.driver_name || '-'}</Td>
                                                                            <Td>{row.equipment_nbr || '-'}</Td>
                                                                            <Td whiteSpace="normal" maxW="150px">{row.loader_names || '-'}</Td>
                                                                            <Td whiteSpace="normal" maxW="150px">{row.mover_names || '-'}</Td>
                                                                            <Td>{row.ticket_no || '-'}</Td>

                                                                            <Td borderLeft="1px solid" borderColor="gray.100" isNumeric textAlign="right">
                                                                                {formatInteger(row.bcc_bunch)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">{formatInteger(row.spb_bunch)}</Td>
                                                                            <Td isNumeric textAlign="right">
                                                                                {row.jjg_restan > 0 ? (
                                                                                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                                                                        {row.jjg_restan}
                                                                                    </Badge>
                                                                                ) : (
                                                                                    '-'
                                                                                )}
                                                                            </Td>

                                                                            <Td borderLeft="1px solid" borderColor="gray.100" isNumeric textAlign="right">
                                                                                {formatInteger(row.bcc_weight)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">{formatInteger(row.spb_weight)}</Td>
                                                                            <Td isNumeric textAlign="right">
                                                                                {row.restan_weight > 0 ? (
                                                                                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                                                                        {formatInteger(row.restan_weight)}
                                                                                    </Badge>
                                                                                ) : (
                                                                                    '-'
                                                                                )}
                                                                            </Td>
                                                                        </Tr>
                                                                        {isExpanded && row.tph_details && row.tph_details.length > 0 && (
                                                                            <TphDetailRows
                                                                                details={row.tph_details}
                                                                                onPreviewImage={handlePreviewImage}
                                                                                wrapColSpan={14}
                                                                            />
                                                                        )}
                                                                    </React.Fragment>
                                                                );
                                                            })}

                                                            {/* Division Subtotals Row */}
                                                            <Tr bg="#e7efdf" fontWeight="bold">
                                                                <Td colSpan={8}>TOTAL {divName}</Td>
                                                                <Td borderLeft="1px solid" borderColor="blackAlpha.300" isNumeric textAlign="right">
                                                                    {formatInteger(division.subtotal.total_bcc_bunch)}
                                                                </Td>
                                                                <Td isNumeric textAlign="right">
                                                                    {formatInteger(division.subtotal.total_spb_bunch)}
                                                                </Td>
                                                                <Td isNumeric textAlign="right">
                                                                    {division.subtotal.total_jjg_restan > 0 ? formatInteger(division.subtotal.total_jjg_restan) : '-'}
                                                                </Td>
                                                                <Td borderLeft="1px solid" borderColor="blackAlpha.300" isNumeric textAlign="right">
                                                                    {formatInteger(division.subtotal.total_bcc_weight)}
                                                                </Td>
                                                                <Td isNumeric textAlign="right">
                                                                    {formatInteger(division.subtotal.total_spb_weight)}
                                                                </Td>
                                                                <Td isNumeric textAlign="right">
                                                                    {division.subtotal.total_restan_weight > 0 ? formatInteger(division.subtotal.total_restan_weight) : '-'}
                                                                </Td>
                                                            </Tr>
                                                        </Tbody>
                                                    </Table>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </Collapse>
                        </Box>
                    );
                })}

                <ImagePreviewModal
                    isOpen={isOpen}
                    onClose={onClose}
                    previewImageUrl={previewImageUrl}
                    previewImageTitle={previewImageTitle}
                />
            </VStack>
        );
    }

    // ==========================================
    // BLOCK VIEW
    // ==========================================
    return (
        <VStack spacing={8} align="stretch">
            {data.map((estate) => {
                const estateKey = `block-estate-${estate.estate_id || estate.estate_name}`;
                const isOpenEstate = isEstateOpen(estateKey, isSingleEstateBlock);

                return (
                    <Box key={estateKey}>
                        {/* LEVEL 1: ESTATE CARD */}
                        <Box borderRadius="lg" boxShadow="sm" overflow="hidden" bg="white" border="1px solid" borderColor="green.200">
                            <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                                <Box
                                    bg="#dbe7d0"
                                    p={4}
                                    flex="2.5"
                                    cursor="pointer"
                                    onClick={() => toggleEstate(estateKey, isSingleEstateBlock)}
                                    _hover={{ bg: '#d0dfc3' }}
                                    transition="background 0.2s"
                                >
                                    <HStack justify="space-between" align="center">
                                        <Box>
                                            <Text color="black" fontSize="md">
                                                <b>ESTATE | - </b>{estate.estate_name || 'TANPA KEBUN'}
                                            </Text>
                                            <Text color="gray.700" fontSize="xs">
                                                {estate.divisions.length} Divisi Terdaftar
                                            </Text>
                                        </Box>
                                        <IconButton
                                            aria-label="Toggle Estate"
                                            icon={isOpenEstate ? <ChevronDownIcon boxSize={5} /> : <ChevronRightIcon boxSize={5} />}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="green"
                                        />
                                    </HStack>
                                </Box>

                                <Box bg="white" p={4} flex="1">
                                    <HStack spacing={4}>
                                        <Box bg="#ededed" p={2} borderRadius="lg">
                                            <Image src={angkongIcon} alt="Angkong" boxSize="25px" />
                                        </Box>
                                        <Box>
                                            <Text color="black" fontSize="xs"><b>Hasil Panen </b>BCC</Text>
                                            <Text color="black" fontSize="sm"><b>{formatInteger(estate.subtotal.total_bcc_weight)} </b>Kg</Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box bg="white" p={4} flex="1">
                                    <HStack spacing={4}>
                                        <Box bg="#ededed" p={2} borderRadius="lg">
                                            <Image src={percentIcon} alt="Realisasi" boxSize="25px" />
                                        </Box>
                                        <Box>
                                            <Text color="black" fontSize="xs"><b>Realisasi </b></Text>
                                            <Text color="black" fontSize="sm"><b>{formatNumber(estate.subtotal.realisasi)}</b> %</Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box bg="white" p={4} flex="1">
                                    <HStack spacing={4}>
                                        <Box bg="red.100" p={2} borderRadius="lg">
                                            <Image src={cautionIcon} alt="Caution" boxSize="25px" />
                                        </Box>
                                        <Box>
                                            <Text color="black" fontSize="xs"><b>Total Restan </b></Text>
                                            <Text color="black" fontSize="sm"><b>{formatInteger(estate.subtotal.total_restan_weight)} </b>Kg</Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box bg="white" p={4} flex="1">
                                    <HStack spacing={4}>
                                        <Box bg="#ededed" p={2} borderRadius="lg">
                                            <Image src={dtIcon} alt="Ritase" boxSize="25px" />
                                        </Box>
                                        <Box>
                                            <Text color="black" fontSize="xs"><b>Ritase </b></Text>
                                            <Text color="black" fontSize="sm"><b>{estate.subtotal.ritase || '-'} </b></Text>
                                        </Box>
                                    </HStack>
                                </Box>
                            </Flex>

                            {/* 1-Row Estate Summary Table */}
                            <Box overflowX="auto" bg="#f7faf4" borderTop="1px solid" borderColor="green.200">
                                <Table variant="unstyled" size="sm">
                                    <Thead>
                                        <Tr bg="#4A5A33">
                                            <Th colSpan={2} py={1.5} px={3}>
                                                <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                    Tenaga Kerja
                                                </Text>
                                            </Th>
                                            <Th colSpan={2} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                    Luas (Ha)
                                                </Text>
                                            </Th>
                                            <Th colSpan={3} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                    Jumlah
                                                </Text>
                                            </Th>
                                            <Th colSpan={4} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                    Janjang
                                                </Text>
                                            </Th>
                                            <Th colSpan={4} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                <Text color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                    Berat Estimasi (kg)
                                                </Text>
                                            </Th>
                                        </Tr>
                                        <Tr bg="#e7efdf" fontSize="xs">
                                            <Th py={1} isNumeric textAlign="right">HK RKH</Th>
                                            <Th py={1} isNumeric textAlign="right">HK BCC</Th>
                                            <Th py={1} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Luas RKH (ha)</Th>
                                            <Th py={1} isNumeric textAlign="right">Luas BKM (ha)</Th>
                                            <Th py={1} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Jml BCC</Th>
                                            <Th py={1} isNumeric textAlign="right">Jml SPB</Th>
                                            <Th py={1} isNumeric textAlign="right">BCC Restan</Th>
                                            <Th py={1} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Janjang RKH</Th>
                                            <Th py={1} isNumeric textAlign="right">Janjang BCC</Th>
                                            <Th py={1} isNumeric textAlign="right">Janjang SPB</Th>
                                            <Th py={1} isNumeric textAlign="right">Janjang Restan</Th>
                                            <Th py={1} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Berat RKH (kg)</Th>
                                            <Th py={1} isNumeric textAlign="right">Berat BCC (kg)</Th>
                                            <Th py={1} isNumeric textAlign="right">Berat SPB (kg)</Th>
                                            <Th py={1} isNumeric textAlign="right">Berat Restan (kg)</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        <Tr bg="white" borderTop="1px solid" borderColor="green.200">
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_rkh_hk || 0)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_bcc_hk || 0)}
                                            </Td>
                                            <Td py={2} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">
                                                {formatNumber(estate.subtotal.total_rkh_harvest_area)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_bkm_harvest_area)}
                                            </Td>
                                            <Td py={2} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">
                                                {formatNumber(estate.subtotal.total_bcc_cnt)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_spb_cnt)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_bcc_restan)}
                                            </Td>
                                            <Td py={2} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">
                                                {formatNumber(estate.subtotal.total_rkh_bunch)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_bcc_bunch)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_spb_bunch)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_jjg_restan)}
                                            </Td>
                                            <Td py={2} isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">
                                                {formatNumber(estate.subtotal.total_rkh_weight)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_bcc_weight)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {formatNumber(estate.subtotal.total_spb_weight)}
                                            </Td>
                                            <Td py={2} isNumeric textAlign="right">
                                                {estate.subtotal.total_restan_weight > 0 ? (
                                                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                                        {formatNumber(estate.subtotal.total_restan_weight)}
                                                    </Badge>
                                                ) : (
                                                    formatNumber(estate.subtotal.total_restan_weight)
                                                )}
                                            </Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            </Box>
                        </Box>

                        {/* LEVEL 2: DIVISION CARDS & DETAIL TABLES */}
                        <Collapse in={isOpenEstate} animateOpacity>
                            <VStack spacing={6} align="stretch" mt={4} pl={{ base: 2, md: 4 }} borderLeft={{ base: 'none', md: '3px solid #c8dcb8' }}>
                                {estate.divisions.map((rkhRecord) => {
                                    return (
                                        <Box key={rkhRecord.rkh_nbr}>
                                            {/* Division Header Card */}
                                            <Box borderRadius="lg" boxShadow="sm" mb={0} overflow="hidden" bg="white">
                                                <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                                                    <Box bg="#e7efdf" p={4} flex="2.5">
                                                        <Text color="black"><b>DIVISI | - </b>{rkhRecord.division}</Text>
                                                        <Text color="black" whiteSpace="nowrap">{rkhRecord.rkh_nbr}</Text>
                                                    </Box>
                                                    <Box bg="white" p={4} flex="1">
                                                        <HStack spacing={4}>
                                                            <Box bg="#ededed" p={2} borderRadius="lg">
                                                                <Image src={angkongIcon} alt="Angkong" boxSize="25px" />
                                                            </Box>
                                                            <Box>
                                                                <Text color="black" fontSize="xs"><b>Hasil Panen </b>BCC</Text>
                                                                <Text color="black" fontSize="sm"><b>{formatInteger(rkhRecord.subtotal.total_bcc_weight)} </b>Kg</Text>
                                                            </Box>
                                                        </HStack>
                                                    </Box>

                                                    <Box bg="white" p={4} flex="1">
                                                        <HStack spacing={4}>
                                                            <Box bg="#ededed" p={2} borderRadius="lg">
                                                                <Image src={percentIcon} alt="Realisasi" boxSize="25px" />
                                                            </Box>
                                                            <Box>
                                                                <Text color="black" fontSize="xs"><b>Realisasi </b></Text>
                                                                <Text color="black" fontSize="sm"><b>{formatNumber(rkhRecord.subtotal.realisasi)}</b> %</Text>
                                                            </Box>
                                                        </HStack>
                                                    </Box>

                                                    <Box bg="white" p={4} flex="1">
                                                        <HStack spacing={4}>
                                                            <Box bg="red.100" p={2} borderRadius="lg">
                                                                <Image src={cautionIcon} alt="Caution" boxSize="25px" />
                                                            </Box>
                                                            <Box>
                                                                <Text color="black" fontSize="xs"><b>Total Restan </b></Text>
                                                                <Text color="black" fontSize="sm"><b>{formatInteger(rkhRecord.subtotal.total_restan_weight)} </b>Kg</Text>
                                                            </Box>
                                                        </HStack>
                                                    </Box>

                                                    <Box bg="white" p={4} flex="1">
                                                        <HStack spacing={4}>
                                                            <Box bg="#ededed" p={2} borderRadius="lg">
                                                                <Image src={dtIcon} alt="Ritase" boxSize="25px" />
                                                            </Box>
                                                            <Box>
                                                                <Text color="black" fontSize="xs"><b>Ritase </b></Text>
                                                                <Text color="black" fontSize="sm"><b>{rkhRecord.subtotal.ritase} </b></Text>
                                                            </Box>
                                                        </HStack>
                                                    </Box>
                                                </Flex>
                                            </Box>

                                            {/* Division Block Detail Table */}
                                            <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm" mt={3}>
                                                <Table variant="unstyled" size="sm">
                                                    <Thead bg="white">
                                                        <Tr bg="#4A5A33" borderTopLeftRadius="lg" borderTopRightRadius="lg">
                                                            <Th colSpan={2} py={2} px={3}>
                                                                <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                    Informasi Blok
                                                                </Text>
                                                            </Th>
                                                            <Th colSpan={2} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                                <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                    Tenaga Kerja
                                                                </Text>
                                                            </Th>
                                                            <Th colSpan={2} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                                <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                    Luas (Ha)
                                                                </Text>
                                                            </Th>
                                                            <Th colSpan={3} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                                <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                    Jumlah
                                                                </Text>
                                                            </Th>
                                                            <Th colSpan={4} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                                <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                    Janjang
                                                                </Text>
                                                            </Th>
                                                            <Th colSpan={4} borderLeft="1px solid" borderColor="whiteAlpha.300">
                                                                <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                                    Berat Estimasi (kg)
                                                                </Text>
                                                            </Th>
                                                        </Tr>
                                                        <Tr bg="#e7efdf">
                                                            <Th><Text whiteSpace="nowrap">Kode Blok</Text></Th>
                                                            <Th>Carry Over</Th>
                                                            <Th isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">HK RKH</Th>
                                                            <Th isNumeric textAlign="right">HK BCC</Th>
                                                            <Th isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Luas RKH (ha)</Th>
                                                            <Th isNumeric textAlign="right">Luas BKM (ha)</Th>
                                                            <Th isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Jml BCC</Th>
                                                            <Th isNumeric textAlign="right">Jml SPB</Th>
                                                            <Th isNumeric textAlign="right">BCC Restan</Th>
                                                            <Th isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Janjang RKH</Th>
                                                            <Th isNumeric textAlign="right">Janjang BCC</Th>
                                                            <Th isNumeric textAlign="right">Janjang SPB</Th>
                                                            <Th isNumeric textAlign="right">Janjang Restan</Th>
                                                            <Th isNumeric borderLeft="1px solid" borderColor="blackAlpha.300" textAlign="right">Berat RKH (kg)</Th>
                                                            <Th isNumeric textAlign="right">Berat BCC (kg)</Th>
                                                            <Th isNumeric textAlign="right">Berat SPB (kg)</Th>
                                                            <Th isNumeric textAlign="right">Berat Restan (kg)</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {rkhRecord.locations && rkhRecord.locations.length > 0 ? (
                                                            rkhRecord.locations.map((location, index) => {
                                                                const rowKey = `${rkhRecord.rkh_nbr}-${location.block_code}`;
                                                                const isExpanded = expandedRows[rowKey] || false;
                                                                return (
                                                                    <React.Fragment key={`${rowKey}-${index}`}>
                                                                        <Tr _hover={{ bg: 'gray.50' }}>
                                                                            <Td>
                                                                                <HStack spacing={2}>
                                                                                    {location.tph_details && location.tph_details.length > 0 && (
                                                                                        <IconButton
                                                                                            aria-label="Expand row"
                                                                                            icon={isExpanded ? <ChevronDownIcon boxSize={4} /> : <ChevronRightIcon boxSize={4} />}
                                                                                            size="xs"
                                                                                            variant="ghost"
                                                                                            onClick={() => toggleRow(rowKey)}
                                                                                        />
                                                                                    )}
                                                                                    <Text fontWeight="bold" fontSize="xs" color="gray.700">
                                                                                        {location.block_code}
                                                                                    </Text>
                                                                                </HStack>
                                                                            </Td>
                                                                            <Td fontSize="xs">
                                                                                {location.is_carry_over ? (
                                                                                    <CheckIcon boxSize={4} color="green.500" />
                                                                                ) : (
                                                                                    <Text>-</Text>
                                                                                )}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                                {formatNumber(location.rkh_hk)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">
                                                                                {formatNumber(location.bcc_hk)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                                {formatNumber(location.rkh_harvest_area)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">
                                                                                {Number(location.bkm_harvest_area) < Number(location.rkh_harvest_area) ? (
                                                                                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                                                                        {formatNumber(location.bkm_harvest_area)}
                                                                                    </Badge>
                                                                                ) : (
                                                                                    formatNumber(location.bkm_harvest_area)
                                                                                )}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                                {formatNumber(location.bcc_cnt)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">{formatNumber(location.spb_cnt)}</Td>
                                                                            <Td isNumeric textAlign="right">{formatNumber(location.bcc_restan)}</Td>
                                                                            <Td isNumeric textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                                {formatNumber(location.rkh_bunch)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">{formatNumber(location.bcc_bunch)}</Td>
                                                                            <Td isNumeric textAlign="right">{formatNumber(location.spb_bunch)}</Td>
                                                                            <Td isNumeric textAlign="right">{formatNumber(location.jjg_restan)}</Td>
                                                                            <Td isNumeric textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                                {formatInteger(location.rkh_weight)}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">
                                                                                {Number(location.bcc_weight) < Number(location.rkh_weight) ? (
                                                                                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                                                                        {formatInteger(location.bcc_weight)}
                                                                                    </Badge>
                                                                                ) : (
                                                                                    formatInteger(location.bcc_weight)
                                                                                )}
                                                                            </Td>
                                                                            <Td isNumeric textAlign="right">{formatInteger(location.spb_weight)}</Td>
                                                                            <Td isNumeric textAlign="right">{formatInteger(location.restan_weight)}</Td>
                                                                        </Tr>

                                                                        {isExpanded && location.tph_details && location.tph_details.length > 0 && (
                                                                            <TphDetailRows
                                                                                details={location.tph_details}
                                                                                onPreviewImage={handlePreviewImage}
                                                                            />
                                                                        )}
                                                                    </React.Fragment>
                                                                );
                                                            })
                                                        ) : (
                                                            <Tr>
                                                                <Td colSpan={17} py={6}>
                                                                    <VStack spacing={1} justify="center" opacity={0.6}>
                                                                        <Text fontSize="sm" fontWeight="medium" color="gray.500">
                                                                            Tidak ada lokasi untuk RKH ini
                                                                        </Text>
                                                                    </VStack>
                                                                </Td>
                                                            </Tr>
                                                        )}

                                                        {/* Subtotal Row */}
                                                        {rkhRecord.locations && rkhRecord.locations.length > 0 && (
                                                            <Tr bg="#e7efdf" borderTop="1px solid" borderColor="blackAlpha.100" borderBottomLeftRadius="lg" borderBottomRightRadius="lg">
                                                                <Td colSpan={2} fontWeight="bold" borderBottomLeftRadius="lg">
                                                                    TOTAL
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                    {formatNumber(rkhRecord.subtotal.total_rkh_hk || 0)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatNumber(rkhRecord.subtotal.total_bcc_hk || 0)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                    {formatNumber(rkhRecord.subtotal.total_rkh_harvest_area)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatNumber(rkhRecord.subtotal.total_bkm_harvest_area)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                    {formatNumber(rkhRecord.subtotal.total_bcc_cnt)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatNumber(rkhRecord.subtotal.total_spb_cnt)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatNumber(rkhRecord.subtotal.total_bcc_restan)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                    {formatNumber(rkhRecord.subtotal.total_rkh_bunch)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatNumber(rkhRecord.subtotal.total_bcc_bunch)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatNumber(rkhRecord.subtotal.total_spb_bunch)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatNumber(rkhRecord.subtotal.total_jjg_restan)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right" borderLeft="1px solid" borderColor="blackAlpha.300">
                                                                    {formatInteger(rkhRecord.subtotal.total_rkh_weight)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatInteger(rkhRecord.subtotal.total_bcc_weight)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right">
                                                                    {formatInteger(rkhRecord.subtotal.total_spb_weight)}
                                                                </Td>
                                                                <Td isNumeric fontWeight="bold" textAlign="right" borderBottomRightRadius="lg">
                                                                    {formatInteger(rkhRecord.subtotal.total_restan_weight)}
                                                                </Td>
                                                            </Tr>
                                                        )}
                                                    </Tbody>
                                                </Table>
                                            </Box>

                                            {/* Footer */}
                                            {rkhRecord.locations && rkhRecord.locations.length > 0 && (
                                                <Box mt={2} px={1}>
                                                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                                                        DIVISI <Text as="span" fontWeight="bold" color="gray.700">{rkhRecord.division}</Text>{' '}
                                                        /{' '}
                                                        <Text as="span" fontWeight="bold" color="gray.700">{rkhRecord.rkh_nbr}</Text>{' '}
                                                        / TANGGAL:{' '}
                                                        <Text as="span" fontWeight="bold" color="gray.700">{formatDate(rkhRecord.rkh_date)}</Text>{' '}
                                                        / USER{' '}
                                                        <Text as="span" fontWeight="bold" color="gray.700">{rkhRecord.rkh_user}</Text>{' '}
                                                        / STAGE{' '}
                                                        <Text as="span" fontWeight="bold" color={rkhRecord.rkh_stage === 'C' ? 'green.500' : rkhRecord.rkh_stage === 'P' ? 'yellow.500' : 'gray.700'}>
                                                            {rkhRecord.rkh_stage}
                                                        </Text>
                                                    </Text>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </VStack>
                        </Collapse>
                    </Box>
                );
            })}

            {/* Image Preview Modal */}
            <ImagePreviewModal
                isOpen={isOpen}
                onClose={onClose}
                previewImageUrl={previewImageUrl}
                previewImageTitle={previewImageTitle}
            />
        </VStack>
    );
};
